<template>
  <CoreLineChart v-if="options" :options="options"></CoreLineChart>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import COLORS from '@/util/colors'
export default {
  props: {
    intervals: {
      type: Array,
      required: false,
      default: undefined
    },
    calculated: {
      type: String,
      required: false,
      default: undefined
    },
    intervalLengthSeconds: {
      type: Number,
      required: false,
      default: 900
    },
    rampUpMinutes: {
      type: Number,
      required: false,
      default: 30
    },
    rampDownMinutes: {
      type: Number,
      required: false,
      default: 15
    }
  },
  data() {
    return {
      options: false,
      includeValueAxis: true
    }
  },
  computed: {
    ...mapGetters([
      'getFacilityTimezone',
      'getFacilityName',
      'getFacilityId',
      'getNcpCeiling'
    ])
  },
  watch: {
    intervals() {
      console.log('intervals changed in chart')
    }
  },
  created() {
    if (typeof this.intervals != 'undefined') {
      this.parse()
    }
  },
  methods: {
    parse() {
      let chartData = {
        xaxis: [],
        load: [],
        targetOutput: [],
        actualOutput: [],
        achievedOutput: [],
        unrealizedOutput: [],
        valueAtStake: [],
        actualValueAtStake: [],
        guidance: [],
        x_plotlines: [],
        y_plotlines: []
      }
      let numRampUpIntervals = parseInt(
        (this.rampUpMinutes * 60) / this.intervalLengthSeconds
      )
      let numRampDownIntervals = parseInt(
        (this.rampDownMinutes * 60) / this.intervalLengthSeconds
      )

      let inRunEvent = false
      this.includeValueAxis = false
      for (var i = 0; i < this.intervals.length; i++) {
        let interval = this.intervals[i]
        chartData.xaxis.push(
          moment(interval.ending_local).format('MM/DD  HH:mm')
        )
        chartData.load.push(parseInt(interval.load_kw))
        chartData.targetOutput.push(parseInt(interval.optimal_power_output_kw))
        chartData.actualOutput.push(parseInt(interval.actual_power_output_kw))
        chartData.valueAtStake.push(parseInt(interval.value_at_stake))
        if (parseInt(interval.value_at_stake)) {
          this.includeValueAxis = true
        }
        chartData.actualValueAtStake.push(
          parseInt(interval.actual_value_at_stake)
        )

        // using real unrealized even if negative; chart axis set min=0, so will
        //   be charted as 0, not actual negative value
        chartData.unrealizedOutput.push(interval.unrealized_power_output_kw)

        chartData.achievedOutput.push(
          parseInt(interval.achieved_power_output_percent * 100)
        )
        chartData.guidance.push(interval.guidance)

        // ** INTERVAL BASED PLOTLINES ** //
        // if guidance = MUST_RUN | DID_RUN
        //     if (NOT tracking run event already)
        //         track run event
        //         create a plot line at (x - # minute intervals for ramp up)
        //     end if
        // else (guidance not MUST_RUN | DID_RUN )
        //     if (tracking run event already)
        //         stop tracking run event
        //         create a plot line at (x+ # minute intervals for ramp down)
        //     end if
        // end if-else
        if (
          interval.guidance === 'MUST_RUN' ||
          interval.guidance === 'DID_RUN'
        ) {
          if (!inRunEvent) {
            inRunEvent = true
            let idx = i - numRampUpIntervals
            if (idx >= 0) {
              let plot = {
                color: COLORS.hex.rampUp,
                width: 2,
                value: idx,
                dashStyle: 'ShortDot',
                label: { text: 'RAMP UP', textAlign: 'left' }
              }
              chartData.x_plotlines.push(plot)
            }
          }
        } else {
          if (inRunEvent) {
            inRunEvent = false
            let idx = i + numRampDownIntervals - 1 // account for post interval (-1)
            if (idx >= 0 && idx < this.intervals.length) {
              let plot = {
                color: COLORS.hex.rampDown,
                width: 2,
                value: idx,
                dashStyle: 'ShortDot',
                label: { text: 'RAMP DOWN', textAlign: 'left' }
              }
              chartData.x_plotlines.push(plot)
            }
          }
        }
      }
      this.build(chartData)
    },
    build(chartData) {
      this.options = {
        title: {
          text: 'Load Forecast (kW), Target Output (kW) and Value Performance'
        },
        subtitle: {
          text: 'Last Calculated: ' + this.calculated
        },
        exporting: {
          filename:
            this.getFacilityName.replace(new RegExp(' ', 'g'), '_') +
            '__Performance'
        },
        xAxis: {
          type: 'datetime',
          categories: chartData.xaxis,
          crosshair: true,
          plotLines: chartData.x_plotlines
        },
        yAxis: [
          {
            // Primary yAxis (left)
            min: 0,
            labels: {
              format: '{value:,.0f} kW',
              style: {
                color: COLORS.hex.power
              }
            },
            title: {
              text: 'Target Output / Load',
              style: {
                color: COLORS.hex.power
              }
            },
            /* horizontal lines to indicate special events on output axis */
            plotLines: chartData.y_plotlines
          },
          {
            // Secondary Axis (right)
            min: 0,
            title: {
              text: (this.includeValueAxis ? 'Value' : ' '),
              style: {
                color: COLORS.hex.value
              }
            },
            labels: {
              format: (this.includeValueAxis ? '${value:,.0f}' : ' '),
              style: {
                color: COLORS.hex.value
              }
            },
            opposite: true
          }
        ],
        tooltip: {
          shared: true,
          crosshairs: true,
          backgroundColor: COLORS.hex.crosshairs,
          borderColor: COLORS.hex.black,
          formatter: function(d) {
            var rV = '<b>Ending: ' + this.x + '</b><br/>' // make a timestanp
            if (!d.isHidden) {
              this.points.forEach(function(d) {
                let val = d.y.toLocaleString()
                if (d.series.name === 'Value') {
                  val = '$' + val
                } else {
                  val += ' kW'
                }
                rV +=
                  '<span style="color:' +
                  d.color +
                  '">\u25CF</span> ' +
                  d.series.name +
                  ': <b> ' +
                  val +
                  '</b><br/>'
              })
            }
            return rV
          }
        },
        series: [
          {
            type: 'spline',
            name: 'Value',
            data: chartData.valueAtStake,
            color: COLORS.hex.value,
            yAxis: 1
          },
          {
            type: 'spline',
            name: 'Actual Value',
            data: chartData.actualValueAtStake,
            color: COLORS.hex.actualValue,
            yAxis: 1
          },
          {
            type: 'spline',
            name: 'Load',
            data: chartData.load,
            color: COLORS.hex.load
          },
          {
            type: 'spline',
            name: 'Target Output',
            data: chartData.targetOutput,
            color: COLORS.hex.power
          },
          {
            type: 'spline',
            name: 'Actual Output',
            data: chartData.actualOutput,
            color: COLORS.hex.actual
          },
          {
            type: 'areaspline', // fill
            name: 'Unrealized Output',
            data: chartData.unrealizedOutput,
            color: COLORS.hex.unrealized,
            fillOpacity: 0.1
          }
        ]
      }
      // console.log('performance.chart options', this.options)
    }
  }
}
</script>
